import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import { Table, TableBody, TableContainer } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import { ContactStatus } from '../../../sic-lib/types/ContactStatus';
import ToggleComponents from '../../Shared/ToggleComponents/ToggleComponents';
import ContactStatusCreate from './ContactStatusCreate/ContactStatusCreate';
import ContactStatusDisplay from './ContactStatusDisplay/ContactStatusDisplay';
import ContactStatusEdit from './ContactStatusEdit/ContactStatusEdit';

interface Props {
    userStatuses: ContactStatus[];
}

export default function ContactStatusSettings({ userStatuses }: Props) {
    const { classes } = useStyles();

    const userStatusList = React.useMemo(() => {
        return [...userStatuses].sort((a, b) => a.status.localeCompare(b.status));
    }, [userStatuses]);

    return (
        <Grid className={classes.root}>
            <Grid container direction="column" wrap="nowrap">
                <Grid item mb={2} xs="auto">
                    <ToggleComponents>
                        {(toggle, setToggle) =>
                            !toggle ? (
                                <Grid>
                                    <Button
                                        className={classes.btnCreateStatus}
                                        onClick={setToggle}
                                        startIcon={<AddIcon />}
                                    >
                                        Create Contact Status
                                    </Button>
                                </Grid>
                            ) : (
                                <Paper>
                                    <div className={classes.header}>
                                        <Typography component="h3" variant="subtitle1">
                                            Create Contact Status
                                        </Typography>
                                    </div>
                                    <div className={classes.header}>
                                        <ContactStatusCreate onToggleHandler={setToggle} />
                                    </div>
                                </Paper>
                            )
                        }
                    </ToggleComponents>
                </Grid>
            </Grid>
            <TableContainer component={Paper}>
                <Table>
                    <TableBody>
                        {userStatusList.map(status => (
                            <ToggleComponents key={status.statusId}>
                                {(toggle, setToggle) =>
                                    !toggle ? (
                                        <ContactStatusDisplay
                                            status={status}
                                            onToggleHandler={setToggle}
                                        />
                                    ) : (
                                        <ContactStatusEdit
                                            status={status}
                                            onToggleHandler={setToggle}
                                        />
                                    )
                                }
                            </ToggleComponents>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        margin: theme.spacing(2),
    },
    header: {
        position: 'relative',
        padding: theme.spacing(2),
        '& > h3': {
            textTransform: 'capitalize',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            display: 'block',
            width: '100%',
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
        },
    },
    btnCreateStatus: {
        color: theme.palette.addButton.main,
        '&:hover': {
            backgroundColor: theme.palette.addButton.light,
        },
    },
}));
